import React, { useState, useEffect } from 'react'
import { EditorState } from 'draft-js'
import Button from 'components/button'
import Menu from 'components/menu'
import { IContextMenuState } from 'types'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { Plus } from 'components/icons'
import { DEFAULT_EMBED_HEIGHT } from 'components/spreadsheet/constants/const'

interface IframeInsertProps {
  editorState: EditorState
  onChange: (nextEditorState: EditorState) => void
  modifier: (editorState: EditorState, url: string, height: number, entityKey?: string) => EditorState
}

export interface AddIframeFormProps extends IframeInsertProps {
  onClose: () => void
  initialData?: {
    src: string
    height: number
    entityKey: string
  }
}

export const AddIframeForm: React.FC<AddIframeFormProps> = ({
  editorState,
  onChange,
  modifier,
  onClose,
  initialData
}: AddIframeFormProps) => {
  // Height isn't set here any more — the iframe is resized by dragging its bottom edge in the document.
  const height = initialData?.height || DEFAULT_EMBED_HEIGHT
  const [url, setUrl] = useState<string>(initialData?.src || '')

  useEffect(() => {
    if (initialData) {
      setUrl(initialData.src)
    }
  }, [initialData])

  const handleSubmit = () => {
    onChange(modifier(editorState, url, height, initialData?.entityKey))
    handleClose()
  }

  const handleClose = () => {
    setUrl('')
    onClose()
  }

  const handleKeyPressed = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSubmit()
    } else if (event.key === 'Escape') {
      handleClose()
    }
  }

  const isEditing = !!initialData

  return (
    <div className="flex py-3 px-5">
      <input
        id="iframe-url"
        placeholder="IFrame URL"
        value={url}
        className="flex-1 mr-2.5"
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setUrl(event.target.value)}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleKeyPressed(event)}
      />
      <Button className="ml-[7px]" onClick={() => handleSubmit()}>
        {isEditing ? 'Update' : 'Add'}
      </Button>
    </div>
  )
}

interface AddIconProps {
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void
}

const AddIframeIcon: React.FC<AddIconProps> = ({ onClick }) => {
  return (
    <div
      className="flex text-base text-body items-center h-full cursor-pointer hover:bg-surface-sunken select-none"
      onClick={onClick}
    >
      <Plus className="mr-[5px]" />
      Iframe
    </div>
  )
}

const IframeInsert: React.FC<IframeInsertProps> = ({ editorState, onChange, modifier }) => {
  const [iframeMenu, setIframeMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)

  const iframeMenuWidth = 500
  const offset = 500

  return (
    <div>
      <AddIframeIcon
        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
          setIframeMenu({
            open: true,
            top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
            left: `${event.currentTarget.getBoundingClientRect().left - iframeMenuWidth + offset}px`,
            right: 'auto',
            bottom: 'auto'
          })
        }}
      />
      {iframeMenu.open && (
        <Menu
          id="iframe-insert-menu"
          menuState={iframeMenu}
          setMenuState={setIframeMenu}
          width={iframeMenuWidth}
          zIndex={1000}
        >
          <AddIframeForm
            editorState={editorState}
            onChange={onChange}
            onClose={() => setIframeMenu(INITIAL_CONTEXT_MENU_STATE)}
            modifier={modifier}
          />
        </Menu>
      )}
    </div>
  )
}

export default IframeInsert
